import { DecimalPipe } from "@angular/common";
import {
    Component,
    forwardRef,
    inject,
    Input,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import Draw from "@arcgis/core/views/draw/Draw";
import MapView from "@arcgis/core/views/MapView";
import AreaMeasurement2DViewModel from "@arcgis/core/widgets/AreaMeasurement2D/AreaMeasurement2DViewModel";
import DistanceMeasurement2DViewModel from "@arcgis/core/widgets/DistanceMeasurement2D/DistanceMeasurement2DViewModel";
import { MeasurementComponentType2D } from "@arcgis/core/widgets/Measurement/types";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { takeUntil } from "rxjs/operators";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ButtonComponent } from "../../../../button/button.component";
import { IconButtonComponent } from "../../../../icon-button/icon-button.component";
import { LabelComponent } from "../../../../label/label.component";
import { ListColumnDirective } from "../../../../list/list-column.directive";
import { ListComponent } from "../../../../list/list.component";
import { SelectorComponent } from "../../../../selector/selector.component";
import { SpanComponent } from "../../../../span/span.component";
import {
    IMapDrawLayer,
    IMapGeometry,
    IMapGeometryPoint,
    IMapGeometryWicket,
    IMapSettings
} from "../../../map.settings";
import { MapCalculationService } from "../../../services/map-calculation.service";
import { TerraformerService } from "../../../services/terraformer.service";
import { Colors } from "../../../symbols/symbol.constants";
import { MapPopupService } from "../../widgets/map-popup/map-popup.service";
import { MapBaseSubComponent } from "../map-base-sub-component/map-base-sub.component";

enum PositionType { Grader = 1, Dms = 2 }

interface IMeasurementResult {
    geometry: IMapGeometryWicket;
    id: string;
    label: IMapGeometryPoint;
    value: number;
}

interface IMeasurementResultAgs {
    geometry: any;
    value: number;
}

interface ITextValue {
    text: string;
    value: number;
}

type MeasurementToolType = MeasurementComponentType2D | "position";

@Component({
    selector: "rask-map-measure",
    templateUrl: "./map-measure.component.html",
    styleUrls: ["./map-measure.component.scss"],
    providers: [
        {
            provide: MapBaseSubComponent,
            useExisting: forwardRef(() => MapMeasureComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        NgbTooltip,
        SelectorComponent,
        FormsModule,
        LabelComponent,
        SpanComponent,
        IconButtonComponent,
        ListComponent,
        ListColumnDirective,
        DecimalPipe
    ]
})

export class MapMeasureComponent extends MapBaseSubComponent {
    readonly areaMultiplierValues: ITextValue[] = [
        {
            text: "Hektar",
            value: 0.0001
        }
    ];
    readonly distanceMultiplierValues: ITextValue[] = [
        {
            text: "Kilometer",
            value: 0.001
        },
        {
            text: "Meter",
            value: 1
        }
    ];
    readonly positionFormatterValues: ITextValue[] = [
        {
            text: "Grader",
            value: PositionType.Grader
        },
        {
            text: "DMS",
            value: PositionType.Dms
        }
    ];

    @Input() area = false;
    areaMultiplier = this.areaMultiplierValues[0];
    widgetId = "measure-widget";
    buttons = [
        {
            iconClass: "esri-icon-measure",
            tooltipText: "Mät",
            visible: () => true,
            widgetId: this.widgetId
        }
    ];
    currentResultUnit = "";
    currentTool: MeasurementToolType;
    currentWatcher: ResourceHandle;
    @Input() distance = false;
    distanceMultiplier = this.distanceMultiplierValues[1];
    lastPositionEvent: any;
    measurementResult: IMeasurementResultAgs;
    measurementResults: IMeasurementResult[];
    @Input() position = false;
    positionFormatter = this.positionFormatterValues[0];
    positionResult: any[];

    private readonly layer: IMapDrawLayer;
    private readonly mapCalcService = inject(MapCalculationService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly terraformerService = inject(TerraformerService);
    private viewModel: AreaMeasurement2DViewModel | DistanceMeasurement2DViewModel;
    private view: any;

    constructor() {
        super();
        this.layer = {
            id: "measureLayer",
            hideInLayerList: true,
            visible: true,
            geometries: [],
            onClick: (mouse, geo) => this.layerClick(mouse, geo)
        };
        this.onHide.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => this.closeMeasurement());

        this.onShow.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.mapPopupService.block();
        });
        this.drawLayers.push(this.layer);
    }

    get canChangeUnit(): boolean {
        return !ObjectUtils.hasValue(this.measurementResults);
    }

    get summary(): number {
        if (!ObjectUtils.hasValue(this.measurementResults)) {
            return undefined;
        }

        const defaultValue = this.measurementResult?.value ?? 0;

        return this.measurementResults.map(x => x.value).reduce((a, b) => a + b, defaultValue);
    }

    deleteCurrentMeasure(): void {
        this.resetMeasurement();
        this.initTool(this.currentTool);
        this.rerenderHistoricalMeasurements();
    }

    deleteMeasure(index: number): void {
        this.measurementResults.splice(index, 1);
        this.rerenderHistoricalMeasurements();

        if (this.measurementResults.length === 0) {
            this.measurementResults = null;
        }
    }

    initMeasure(tool: MeasurementToolType) {
        this.viewModel = tool === "distance"
            ? new DistanceMeasurement2DViewModel({
                view: this.view,
                unit: "meters"
            })
            : new AreaMeasurement2DViewModel({
                view: this.view,
                unit: "hectares"
            });

        this.updateWatcher();

        reactiveUtils.when(() => this.viewModel?.state === "ready", () => {
            this.viewModel.start();
        }, {
            once: true,
            initial: true
        });
    }

    initTool(tool: MeasurementToolType): void {
        if (tool !== "position") {
            this.view.when(() => this.initMeasure(tool));
        } else {
            this.view.surface.style.cursor = "crosshair";
        }
    }

    newMeasure(): void {
        if (!ObjectUtils.hasValue(this.measurementResults)) {
            this.measurementResults = [];
        }

        const guid = GuidUtils.newGuid();
        const measureGeo = {
            id: guid,
            wkt: this.terraformerService.arcGisToWkt(this.measurementResult.geometry),
            styleName: "measureLine",
            visible: true
        } as IMapGeometryWicket;

        const geoCenter = this.measurementResult.geometry.extent.center;
        const measureLabel = {
            id: `${guid}_textsymbol`,
            northing: geoCenter.y,
            easting: geoCenter.x,
            color: Colors.darkgrey,
            symbolText: `${+this.measurementResult.value.toFixed(2)} ${this.currentResultUnit}`,
            symbolHaloColor: Colors.white,
            symbolHaloSize: "1px",
            size: 12,
            selected: true,
            visible: true,
            parent: measureGeo
        } as IMapGeometryPoint;

        this.measurementResults.push({
            id: guid,
            value: this.measurementResult.value,
            geometry: measureGeo,
            label: measureLabel
        });

        this.resetMeasurement();
        this.initTool(this.currentTool);
        this.rerenderHistoricalMeasurements();
    }

    onInit(view: MapView, _draw: Draw): void {
        this.view = view;
    }

    preInit(settings: IMapSettings): void {
        settings.coords = true;
    }

    refreshPosition() {
        if (!this.lastPositionEvent) {
            return;
        }
        this.onPositionUpdate(this.lastPositionEvent);
    }

    toggle(tool: MeasurementToolType): void {
        this.currentTool = tool;
        this.measurementResults = null;
        this.resetMeasurement();
        this.initTool(tool);
    }

    unitChange(selectedMultiplier: ITextValue): void {
        switch (selectedMultiplier?.text) {
            case "Hektar":
                if (this.currentTool === "area") {
                    (this.viewModel as AreaMeasurement2DViewModel).unit = "hectares";
                }
                break;
            case "Meter":
                if (this.currentTool === "distance") {
                    (this.viewModel as DistanceMeasurement2DViewModel).unit = "meters";
                }
                break;
            case "Kilometer":
                if (this.currentTool === "distance") {
                    (this.viewModel as DistanceMeasurement2DViewModel).unit = "kilometers";
                }
                break;
        }
    }

    viewFormatter = (i: ITextValue): string => i.text;

    private closeMeasurement(): void {
        this.currentTool = undefined;
        this.resetMeasurement();
        this.mapPopupService.unblock();
    }

    private formatPosition(value): string {
        if (this.positionFormatter.value === PositionType.Grader) {
            return value.toFixed(6);
        } else {
            return this.mapCalcService.getDmsFromDegrees(value);
        }
    }

    private layerClick(evt, geo): boolean {
        if (this.currentTool === "position") {
            this.onPositionUpdate(evt);
        }

        return false;
    }

    private readonly onAreaUpdate = (measurement): void => {
        this.measurementResult = {
            value: measurement.area * this.areaMultiplier.value,
            geometry: measurement.geometry
        };
        this.currentResultUnit = this.areaMultiplier.text;
    };

    private readonly onDistanceUpdate = (measurement): void => {
        if (!ObjectUtils.hasValue(measurement)) {
            this.measurementResult = null;
            return;
        }

        this.measurementResult = {
            value: measurement.length * this.distanceMultiplier.value,
            geometry: measurement.geometry
        };
        this.currentResultUnit = this.distanceMultiplier.text;
    };

    private putPositionMarker(pos): void {
        const geo = {
            id: "position",
            styleName: "positionMarker",
            northing: pos.y,
            easting: pos.x,
            visible: true
        } as IMapGeometryPoint;
        this.layer.geometries = [geo];
        this.refreshLayers();
    }

    private readonly onPositionUpdate = (evt): void => {
        this.positionResult = [
            {
                x: this.formatPosition(evt.coords.latitude),
                y: this.formatPosition(evt.coords.longitude)
            },
            {
                x: evt.point.x.toFixed(3),
                y: evt.point.y.toFixed(3)
            }
        ];
        this.putPositionMarker(evt.point);
        this.lastPositionEvent = evt;
    };

    private rerenderHistoricalMeasurements(): void {
        let geometries = [];
        if (ObjectUtils.hasValue(this.measurementResults)) {
            const geoms: IMapGeometry[] = this.measurementResults.map(x => x.geometry);
            const labels: IMapGeometry[] = this.measurementResults.map(x => x.label);
            geometries = geoms.concat(labels);
        }

        this.layer.geometries = geometries;
        this.refreshLayers();
    }

    private resetMeasurement(): void {
        if (!this.view) {
            return;
        }
        this.view.surface.style.cursor = "";

        if (this.currentWatcher) {
            this.currentWatcher.remove();
            this.currentWatcher = undefined;
        }

        this.viewModel?.destroy();
        this.viewModel = undefined;

        this.measurementResult = null;
        this.positionResult = null;
        this.lastPositionEvent = null;
        this.layer.geometries = [];

        this.refreshLayers();
    }

    private setWatcher(callback): void {
        this.currentWatcher?.remove();

        this.currentWatcher = reactiveUtils.watch(() => this.viewModel.measurement, (measurement) => {
            callback(measurement);
        });
    }

    private updateWatcher(): void {
        switch (this.currentTool) {
            case "distance":
                this.setWatcher(this.onDistanceUpdate);
                break;
            case "area":
                this.setWatcher(this.onAreaUpdate);
                break;
        }
    }
}
